/**
 * OAuth Diagnostic Utility
 * 
 * This script helps diagnose issues with Google OAuth by adding diagnostic logging
 * and validation functions for OAuth configuration.
 */

// Enable diagnostics by setting this to true
export const OAUTH_DIAGNOSTICS_ENABLED = true;

// Log diagnostic information
export const logDiagnostic = (message: string): void => {
  if (!OAUTH_DIAGNOSTICS_ENABLED) return;
  
  const timestamp = new Date().toISOString();
  const logMessage = `[OAuth Diagnostic ${timestamp}] ${message}`;
  
  // Log to console
  console.log(logMessage);
  
  // Store in localStorage for later retrieval
  try {
    const logs = JSON.parse(localStorage.getItem('oauth_diagnostic_logs') || '[]');
    logs.push(logMessage);
    localStorage.setItem('oauth_diagnostic_logs', JSON.stringify(logs.slice(-50))); // Keep last 50 logs
  } catch (e) {
    console.error('Error storing OAuth diagnostic log:', e);
  }
};

export const validateClientId = (clientId: string): string => {
  if (!OAUTH_DIAGNOSTICS_ENABLED) return clientId;
  
  logDiagnostic(`Using Client ID: ${clientId}`);
  
  // Check that the client ID has a valid format
  if (!clientId.includes('googleusercontent.com')) {
    logDiagnostic(`WARNING: Client ID ${clientId} doesn't appear to be a valid Google OAuth Client ID`);
  }
  
  // Add all known good client IDs from Google Cloud Console here
  const knownGoodId = '205360336037-kd07ma25pjri7up1dpk0dk5on5vrki7r.apps.googleusercontent.com';
  const webClient3Id = ''; // Add the "Web client 3" ID here from Google Cloud Console
  
  if (clientId !== knownGoodId && clientId !== webClient3Id && webClient3Id !== '') {
    logDiagnostic(`WARNING: Client ID ${clientId} doesn't match any known good IDs`);
    logDiagnostic(`Known good IDs: 
    - prest0 Web Client: ${knownGoodId}
    - Web client 3: ${webClient3Id}`);
  }
  
  // Extract current environment and domain for diagnosis
  const currentEnv = process.env.NODE_ENV || 'unknown';
  const currentDomain = window.location.origin;
  
  logDiagnostic(`Current environment: ${currentEnv}`);
  logDiagnostic(`Current domain: ${currentDomain}`);
  
  // Do not modify the ID, just validate it
  return clientId;
};

// Check if we are running in production mode
export const isProduction = (): boolean => {
  return process.env.NODE_ENV === 'production';
};

// Check if we are running on a specific domain
export const isOnDomain = (domain: string): boolean => {
  return window.location.hostname.includes(domain);
};

// Get the recommended client ID for the current environment
export const getRecommendedClientId = (): string => {
  const isLocalhost = window.location.hostname === 'localhost' || 
                      window.location.hostname === '127.0.0.1';
  
  if (isLocalhost) {
    return '205360336037-kd07ma25pjri7up1dpk0dk5on5vrki7r.apps.googleusercontent.com'; // Development Client ID
  } else {
    // Add the production client ID here
    return '205360336037-kd07ma25pjri7up1dpk0dk5on5vrki7r.apps.googleusercontent.com'; // Production Client ID - same as development for now
  }
};

// Clear diagnostic logs
export const clearDiagnosticLogs = (): void => {
  localStorage.removeItem('oauth_diagnostic_logs');
  logDiagnostic('Diagnostic logs cleared');
};

// Get all diagnostic logs
export const getDiagnosticLogs = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem('oauth_diagnostic_logs') || '[]');
  } catch (e) {
    return [];
  }
};

const OAuthDiagnostics = {
  logDiagnostic,
  validateClientId,
  isProduction,
  isOnDomain,
  getRecommendedClientId,
  clearDiagnosticLogs,
  getDiagnosticLogs
};

export default OAuthDiagnostics; 